import { useState, type ReactElement } from "react";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { fetchPlans, formatUsd } from "../billing/api";
import { apiFetch, publicApexHost } from "../lib/api";
import { suggestSlug } from "./lib/start-trial";
import { MarketingLayout } from "./sections/Layout";
import { ENTRY_TIER_CREDITS, type BillingCycle } from "./sections/Pricing";

// Single-step signup → redirect to Stripe Checkout flow:
//   1. Collect business name / slug / email / cycle, POST /api/signup to
//      reserve the slug + create (or recycle) the Stripe customer.
//   2. POST /api/checkout/subscription with {slug} to get a hosted Checkout
//      URL; window.location to Stripe.
//   3. Stripe handles card entry, 3DS, Apple/Google Pay, then bounces the
//      user back to /checkout-success?session_id=... where we send the
//      welcome email and walk them into the owner sign-in flow.

interface SignupFormValues {
  businessName: string;
  slug: string;
  email: string;
  /** E.164 +1XXXXXXXXXX or empty */
  phone: string;
  cycle: BillingCycle;
}

/** Tier carried over from the pricing page. */
interface TierChoice {
  planId: "pro" | "business";
  monthlyCredits: number;
}

/**
 * Normalize loosely-typed US/Canada phone input to E.164 `+1XXXXXXXXXX`.
 * Accepts `8183124251`, `818-312-4251`, `(818) 312-4251`, `1 818 312 4251`,
 * `+1 818 312 4251`, etc. Returns null if it isn't a plausible 10-digit
 * (optionally 1-prefixed) North American number. The server contract stays
 * strict (`^\+1\d{10}$`); this just removes the "you must type +1" friction.
 */
export function normalizeUsPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

async function postSignup(
  values: SignupFormValues & { tier: TierChoice },
): Promise<{ ok: true; slug: string } | { ok: false; error: string }> {
  try {
    const r = await apiFetch("/signup", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "Idempotency-Key": `web:${crypto.randomUUID()}`,
      },
      body: JSON.stringify({
        businessName: values.businessName,
        slug: values.slug,
        email: values.email,
        billingCycle: values.cycle,
        planId: values.tier.planId,
        monthlyCredits: values.tier.monthlyCredits,
        ...(values.phone.trim()
          ? { ownerPhone: normalizeUsPhone(values.phone) ?? values.phone.trim() }
          : {}),
      }),
    });
    if (!r.ok) {
      const body = await r.text();
      return { ok: false, error: body || `Signup failed (${r.status})` };
    }
    const data = (await r.json()) as { slug: string };
    return { ok: true, slug: data.slug };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

async function postCheckout(
  slug: string,
): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  try {
    const r = await apiFetch("/checkout/subscription", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "Idempotency-Key": `web:${crypto.randomUUID()}`,
      },
      body: JSON.stringify({ slug }),
    });
    if (!r.ok) {
      const body = await r.text();
      return { ok: false, error: body || `Checkout failed (${r.status})` };
    }
    const data = (await r.json()) as { url: string };
    if (!data.url) return { ok: false, error: "missing_checkout_url" };
    return { ok: true, url: data.url };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export function Signup(): ReactElement {
  const search = useSearch({ from: "/signup" });
  const initialCycle = search.cycle ?? "annual";
  // Optional pre-fills — useful for sending an existing-tenant owner a
  // one-click "claim your account" link. e.g.
  //   /signup?email=andrew@…&slug=starbuck&business=Starbuck+Jazz+Quartet&cycle=annual
  // Combined with the signup-endpoint's "recycle pending site for matching
  // email+slug" behavior, the recipient just picks a card and confirms on Stripe.
  const initialEmail = search.email ?? "";
  const initialSlug = search.slug ?? "";
  const initialBusinessName = search.business ?? "";
  const intent = search.intent?.trim() ?? "";
  const cancelled = Number(search.cancelled) === 1;
  const tier: TierChoice = {
    planId: search.plan ?? "pro",
    monthlyCredits: search.credits ?? ENTRY_TIER_CREDITS,
  };

  return (
    <MarketingLayout>
      <section className="mx-auto w-full max-w-xl px-6 py-16 sm:py-24">
        <div className="mb-10 text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">
            Spin up your site
          </div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Five minutes from form to live website.
          </h1>
          {intent ? (
            <p className="mt-4 rounded-2xl border border-[#d5e9f7] bg-[#f3f9fe] px-4 py-3 text-left text-sm leading-6 text-[#334641]">
              We&apos;ll start with: “{intent}”
            </p>
          ) : null}
          {cancelled ? (
            <p className="mt-4 text-sm text-muted-foreground">
              No charge — you cancelled the payment step. Pick up where you left off below.
            </p>
          ) : null}
        </div>

        <SignupForm
          initialCycle={initialCycle}
          initialEmail={initialEmail}
          initialSlug={initialSlug}
          initialBusinessName={initialBusinessName}
          tier={tier}
        />
      </section>
    </MarketingLayout>
  );
}

interface SignupFormProps {
  initialCycle: BillingCycle;
  initialEmail?: string;
  initialSlug?: string;
  initialBusinessName?: string;
  tier: TierChoice;
}

function SignupForm({
  initialCycle,
  initialEmail = "",
  initialSlug = "",
  initialBusinessName = "",
  tier,
}: SignupFormProps): ReactElement {
  // If a slug was passed via URL, treat it as user-touched so the
  // business-name auto-suggester doesn't immediately overwrite it.
  const [slugTouched, setSlugTouched] = useState(Boolean(initialSlug));
  // Agency handoff: when the URL pre-fills slug + business name, the site is
  // already provisioned in DDB under that slug with the recipient's email as
  // pending owner. Editing the slug would orphan that work and create a new
  // empty site, so lock both fields when prefilled.
  const slugLocked = Boolean(initialSlug);
  const businessNameLocked = Boolean(initialBusinessName);
  const [serverError, setServerError] = useState<string | null>(null);
  const apexHost = publicApexHost();
  const planLabel = tier.planId === "business" ? "Business" : "Pro";
  const catalog = useQuery({
    queryKey: ["plan-catalog"],
    queryFn: fetchPlans,
    staleTime: 10 * 60 * 1000,
  });
  const prices = catalog.data?.plans
    .find((plan) => plan.id === tier.planId)
    ?.tiers.find((t) => t.monthlyCredits === tier.monthlyCredits);
  const signupMutation = useMutation({
    mutationFn: postSignup,
  });
  const checkoutMutation = useMutation({
    mutationFn: postCheckout,
  });
  const form = useForm({
    defaultValues: {
      businessName: initialBusinessName,
      slug: initialSlug,
      email: initialEmail,
      phone: "",
      cycle: initialCycle,
    } satisfies SignupFormValues,
    onSubmit: async ({ value }) => {
      setServerError(null);
      const signup = await signupMutation.mutateAsync({ ...value, tier });
      if (!signup.ok) {
        setServerError(signup.error);
        return;
      }
      const checkout = await checkoutMutation.mutateAsync(signup.slug);
      if (!checkout.ok) {
        setServerError(checkout.error);
        return;
      }
      // Hand off to Stripe — Stripe Checkout bounces back to /checkout-success.
      window.location.assign(checkout.url);
    },
  });

  return (
    <Card>
      <CardContent className="p-6 sm:p-8">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
          noValidate
          className="space-y-5"
        >
          <form.Field
            name="businessName"
            validators={{
              onSubmit: ({ value }) =>
                value.trim() ? undefined : "What's your business called?",
            }}
          >
            {(field) => {
              const error = field.state.meta.errors[0];
              return (
                <div className="space-y-1.5">
                  <Label htmlFor="businessName">Business name</Label>
                  <Input
                    id="businessName"
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => {
                      const nextBusinessName = event.currentTarget.value;
                      field.handleChange(nextBusinessName);
                      if (!slugTouched) {
                        form.setFieldValue(
                          "slug",
                          suggestSlug(nextBusinessName),
                          { dontUpdateMeta: true, dontValidate: true },
                        );
                      }
                    }}
                    placeholder="Starbuck Jazz Quartet"
                    autoComplete="organization"
                    required
                    readOnly={businessNameLocked}
                    aria-invalid={Boolean(error)}
                    className={
                      businessNameLocked ? "bg-muted cursor-not-allowed" : undefined
                    }
                  />
                  {typeof error === "string" ? (
                    <p className="text-xs text-destructive">{error}</p>
                  ) : null}
                </div>
              );
            }}
          </form.Field>

          <form.Field
            name="slug"
            validators={{
              onSubmit: ({ value }) => {
                if (!value || value.length < 3) {
                  return "At least 3 characters, lowercase letters, numbers, or hyphens.";
                }
                if (!/^[a-z0-9-]+$/.test(value)) {
                  return "Lowercase letters, numbers, and hyphens only.";
                }
                return undefined;
              },
            }}
          >
            {(field) => {
              const error = field.state.meta.errors[0];
              return (
                <div className="space-y-1.5">
                  <Label htmlFor="slug">Site address</Label>
                  <Input
                    id="slug"
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => {
                      setSlugTouched(true);
                      field.handleChange(event.currentTarget.value.toLowerCase());
                    }}
                    placeholder="starbuck"
                    required
                    readOnly={slugLocked}
                    aria-invalid={Boolean(error)}
                    className={
                      slugLocked ? "bg-muted cursor-not-allowed" : undefined
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Your site lives at{" "}
                    <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                      {field.state.value || "yourname"}.{apexHost}
                    </code>
                    {slugLocked
                      ? " — your site is already set up at this address."
                      : " — bring your own domain anytime."}
                  </p>
                  {typeof error === "string" ? (
                    <p className="text-xs text-destructive">{error}</p>
                  ) : null}
                </div>
              );
            }}
          </form.Field>

          <form.Field
            name="email"
            validators={{
              onSubmit: ({ value }) => {
                if (!value.trim()) return "We need an email so you can sign in.";
                if (!/.+@.+\..+/.test(value)) return "That email doesn't look right.";
                return undefined;
              },
            }}
          >
            {(field) => {
              const error = field.state.meta.errors[0];
              return (
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name={field.name}
                    type="email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.currentTarget.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    aria-invalid={Boolean(error)}
                  />
                  {typeof error === "string" ? (
                    <p className="text-xs text-destructive">{error}</p>
                  ) : null}
                </div>
              );
            }}
          </form.Field>

          <form.Field
            name="phone"
            validators={{
              onSubmit: ({ value }) => {
                if (!value.trim()) return undefined;
                return normalizeUsPhone(value)
                  ? undefined
                  : "Enter a 10-digit US/Canada phone number.";
              },
            }}
          >
            {(field) => {
              const error = field.state.meta.errors[0];
              return (
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    name={field.name}
                    type="tel"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.currentTarget.value)}
                    placeholder="(818) 312-4251"
                    autoComplete="tel"
                    aria-invalid={Boolean(error)}
                  />
                  <p className="text-xs text-muted-foreground">
                    So you can text your site changes. We only text you back
                    about your site — never promotional texts.
                  </p>
                  {typeof error === "string" ? (
                    <p className="text-xs text-destructive">{error}</p>
                  ) : null}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="cycle">
            {(field) => (
              <div className="space-y-1.5">
                <Label>Billing</Label>
                <Tabs
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value as BillingCycle)}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="monthly">
                      {prices ? `${formatUsd(prices.monthlyPriceCents)} / month` : "Monthly"}
                    </TabsTrigger>
                    <TabsTrigger value="annual">
                      {prices
                        ? `${formatUsd(prices.annualPriceCents)} / year · 2 months free`
                        : "Annual"}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <p className="text-xs text-muted-foreground">
                  {planLabel} — {tier.monthlyCredits.toLocaleString()} credits a
                  month.{" "}
                  {field.state.value === "annual"
                    ? "Billed once a year."
                    : "Billed monthly. Cancel anytime."}
                </p>
              </div>
            )}
          </form.Field>

          {serverError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </div>
          ) : null}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={signupMutation.isPending || checkoutMutation.isPending}
          >
            {signupMutation.isPending || checkoutMutation.isPending
              ? "Sending you to checkout…"
              : "Continue to checkout"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            14-day free trial. Payment handled by Stripe.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
